import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, finalize } from 'rxjs';
import { LoaderStartConfig, LoaderToken } from '../models/loading.models';
import { LOADER_SKIP_CONTEXT } from '../tokens/http-loader-context.token';
import { LoaderOrchestratorService } from '../services/loader-orchestrator.service';
import { LoaderPolicyService } from '../services/loader-policy.service';

@Injectable()
export class HttpLoaderInterceptor implements HttpInterceptor {
  constructor(
    private readonly loaderOrchestratorService: LoaderOrchestratorService,
    private readonly loaderPolicyService: LoaderPolicyService
  ) {}

  intercept(
    httpRequest: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (httpRequest.context.get(LOADER_SKIP_CONTEXT)) {
      return next.handle(httpRequest);
    }

    const resolvedLoaderPolicy = this.loaderPolicyService.resolveHttpPolicy(httpRequest);

    if (resolvedLoaderPolicy.skip) {
      return next.handle(httpRequest);
    }

    const loaderStartConfig: LoaderStartConfig = {
      mode: resolvedLoaderPolicy.mode,
      scope: resolvedLoaderPolicy.scope,
      owner: resolvedLoaderPolicy.owner,
      delayMs: resolvedLoaderPolicy.delayMs,
      minVisibleMs: resolvedLoaderPolicy.minVisibleMs,
      priority: resolvedLoaderPolicy.priority,
      groupId: resolvedLoaderPolicy.groupId,
      blocking: resolvedLoaderPolicy.blocking,
      message: resolvedLoaderPolicy.message,
      referenceKey: resolvedLoaderPolicy.referenceKey,
      routeKey: resolvedLoaderPolicy.routeKey,
      moduleKey: resolvedLoaderPolicy.moduleKey,
      source: resolvedLoaderPolicy.source,
      metadata: resolvedLoaderPolicy.metadata,
    };

    const loaderToken = resolvedLoaderPolicy.referenceKey
      ? this.loaderOrchestratorService.incrementReference(
          resolvedLoaderPolicy.referenceKey,
          loaderStartConfig
        )
      : this.loaderOrchestratorService.start(loaderStartConfig);

    return next.handle(httpRequest).pipe(
      finalize(() => {
        this.stopResolvedLoader(resolvedLoaderPolicy.referenceKey, loaderToken);
      })
    );
  }

  private stopResolvedLoader(
    referenceKey: string | undefined,
    loaderToken: LoaderToken
  ): void {
    if (referenceKey) {
      this.loaderOrchestratorService.decrementReference(referenceKey);
      return;
    }

    this.loaderOrchestratorService.stop(loaderToken);
  }
}
